import { difference, type Values } from '@openx/components-form/difference.ts';
import {
  mapContextualSegmentsToApi,
  mapContextualSegmentsToFormState,
  splitLegacyContextualSegments,
} from '@openx/components-targeting/Targeting/ContextualSegments/helpers.ts';
import { targetingDimension } from '@openx/components-targeting/Targeting/constants.ts';
import { withNormalizedRenderingContext } from '@openx/components-targeting/Targeting/TargetingPresets/utils/normalizeRenderingContext.ts';
import { emptyStructure as emptyRenderingContextStructure } from '@openx/components-targeting/Targeting/TargetingPresets/utils/transformToTargetApiObject.ts';
import {
  EstimateRangeFields,
  type EstimatesState,
  type PackageEstimates,
  PackageFields,
  type PackageFormState,
  type PackageWithTargeting,
  type PrivateMarket,
} from '@openx/types/package.ts';
import type { CustomVariablesFormState } from '@openx/types/targeting/advancedTargeting.ts';
import { type ContentObjectApi, ContentObjectOptions } from '@openx/types/targeting/contentObject.ts';
import { contextualSegmentApiKey, contextualSegmentPrefix } from '@openx/types/targeting/contextualSegments.ts';
import type { CustomVariablesData } from '@openx/types/targeting/customVariables.ts';
import { type FormatTypeData, InventoryFormatTypeOption } from '@openx/types/targeting/formatType.ts';
import {
  type GeoCirclesApi,
  GeographicOption,
  type GeographicTargetingApi,
} from '@openx/types/targeting/geographic.ts';
import {
  type InventoryAndContentApiRequest,
  InventoryContentOption,
} from '@openx/types/targeting/inventoryAndContent.ts';
import { SelectRateOption } from '@openx/types/targeting/selectRate.ts';
import { IQ_SCORE_VAL, type Targeting, TargetingFields } from '@openx/types/targeting/targeting.ts';
import { Criteria, type CriteriaValue, Intersect } from '@openx/types/targeting/targetingValuesTypes.ts';
import { TechnologyAndDevicesOption } from '@openx/types/targeting/technologyAndDevices.ts';
import cloneDeep from 'lodash/cloneDeep';
import has from 'lodash/has';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import { getInitialPckValues, packageUsedKeys } from '../constants.ts';
import type { InventoryQualityValue } from '../useIqScoringAutoCheck.ts';

const formatPrice = (price: string | null | undefined): string =>
  price
    ? new Intl.NumberFormat('en-EN', {
        maximumFractionDigits: 4,
        minimumFractionDigits: 2,
      }).format(Number(price))
    : '';

export const formatGeographic = (geographic?: GeographicTargetingApi | null): GeographicTargetingApi | null => {
  if (!geographic) return null;

  return geographic;
};

const normalizeGeographicCirclesValues = (circles: GeoCirclesApi): GeoCirclesApi => {
  return {
    val: circles.val.map(v => ({
      lat: Number(v.lat).toFixed(6),
      lon: Number(v.lon).toFixed(6),
      rad: Number(v.rad).toFixed(3),
    })),
  };
};

export const normalizeGeographic = (geographic?: GeographicTargetingApi | null) => {
  const normalizedGeographicOutput = geographic;

  if (normalizedGeographicOutput?.[GeographicOption.CIRCLES]) {
    normalizedGeographicOutput[GeographicOption.CIRCLES] = normalizeGeographicCirclesValues(
      normalizedGeographicOutput[GeographicOption.CIRCLES]!
    );
  }

  return normalizedGeographicOutput ?? null;
};

export function cleanUpTargetingNulls(targetingParams) {
  if (!targetingParams || typeof targetingParams !== 'object') {
    return null;
  }

  Object.entries(targetingParams).forEach(([field, value]) => {
    if (isObject(value)) {
      targetingParams[field] = cleanUpTargetingNulls(value);
    }

    if (targetingParams[field] === null) {
      delete targetingParams[field];
    }
  });

  const targetingSize = Object.keys(targetingParams).length;
  const isSingleDimensionOperator =
    Object.keys(targetingParams).includes(InventoryContentOption.CONTENT_INTER_DIMENSION_OPERATOR) &&
    targetingSize === 1;

  return targetingSize > 0 && !isSingleDimensionOperator ? targetingParams : null;
}

const isRangeValueEmpty = (v: unknown): boolean => v === undefined || v === null || v === '';

const normalizeEstimateValue = (value: string): string => {
  if (isRangeValueEmpty(value)) {
    return value;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue.toFixed(2) : value;
};

const estimateRangeFields = Object.values(EstimateRangeFields);

export const normalizeEstimates = (estimates: EstimatesState | undefined): EstimatesState | undefined => {
  if (!estimates) {
    return estimates;
  }

  return estimateRangeFields.reduce((acc, field) => {
    const fieldValue = estimates[field];
    acc[field] = fieldValue
      ? {
          low: normalizeEstimateValue(fieldValue.low),
          high: normalizeEstimateValue(fieldValue.high),
        }
      : fieldValue;
    return acc;
  }, {} as EstimatesState);
};

export const cleanUpEstimatesRanges = (dirtyEstimatesObject: PackageEstimates) => {
  const estimates = { ...dirtyEstimatesObject };

  estimateRangeFields.forEach(field => {
    const value = estimates[field];
    if (!value || isRangeValueEmpty(value.low) || isRangeValueEmpty(value.high)) {
      estimates[field] = null;
    }
  });

  if (estimates.cpm === null && estimates.reach === null && estimates.market_requests === null) {
    return null;
  }

  return estimates;
};

export const prepareRangeEstimationsDiff = (diff: Values, formValuesToApi) => {
  diff[PackageFields.ESTIMATES] = cleanUpEstimatesRanges({
    [EstimateRangeFields.CPM]: {
      low: formValuesToApi?.estimates?.cpm?.low || '',
      high: formValuesToApi?.estimates?.cpm?.high || '',
    },
    [EstimateRangeFields.REACH]: {
      low: formValuesToApi?.estimates?.reach?.low || '',
      high: formValuesToApi?.estimates?.reach?.high || '',
    },
    [EstimateRangeFields.MARKET_REQUESTS]: {
      low: formValuesToApi?.estimates?.market_requests?.low || '',
      high: formValuesToApi?.estimates?.market_requests?.high || '',
    },
  });
};

const sortFormatTypeValues = (formatTypeTargeting: FormatTypeData | null): FormatTypeData | null => {
  return (
    formatTypeTargeting && {
      ...formatTypeTargeting,
      ...(formatTypeTargeting.ad_placement?.val && {
        ad_placement: {
          op: Intersect.INTERSECTS,
          val: formatTypeTargeting.ad_placement?.val,
        },
      }),
      ...(formatTypeTargeting.distribution_channel?.val && {
        distribution_channel: {
          op: Intersect.INTERSECTS,
          val: formatTypeTargeting.distribution_channel?.val,
        },
      }),
    }
  );
};

export const packageToFormState = (pckgInput: Partial<PackageWithTargeting>): PackageFormState => {
  const pckg = cloneDeep(pckgInput);
  const initValues = getInitialPckValues();
  let inventoryAndContent, viewability, vtr, excludeIndirectValue, includeOpenBiddingInventory, contentObject;

  if (pckg) {
    Object.keys(pckg).forEach(k => {
      if (packageUsedKeys.includes(k) && pckg[k]) {
        switch (k) {
          case PackageFields.PRIVATE_MARKET:
            break;
          case PackageFields.RATE:
            initValues[k] = formatPrice(pckg[k]);
            break;
          case PackageFields.URLS:
            initValues[k] = Object.keys(pckg[k] || {});
            break;
          case TargetingFields.URL_TARGETING:
            initValues[PackageFields.TARGETING][k] = pckg[k] || null;
            break;
          case PackageFields.ESTIMATES:
            initValues[PackageFields.ESTIMATES] = {
              [EstimateRangeFields.CPM]: {
                low: pckg?.estimates?.cpm?.low || '',
                high: pckg?.estimates?.cpm?.high || '',
              },
              [EstimateRangeFields.REACH]: {
                low: pckg?.estimates?.reach?.low || '',
                high: pckg?.estimates?.reach?.high || '',
              },
              [EstimateRangeFields.MARKET_REQUESTS]: {
                low: pckg?.estimates?.market_requests?.low || '',
                high: pckg?.estimates?.market_requests?.high || '',
              },
            };
            break;
          case PackageFields.TARGETING: {
            initValues[k][TargetingFields.ATTENTION] = pckg[k]?.attention || null;
            initValues[k][TargetingFields.VPVH] = pckg[k]?.vpvh || null;

            initValues[k][TargetingFields.DIMENSION] =
              pckg[k]?.inter_dimension_operator === Criteria.ALL ? Criteria.ALL : Criteria.ANY;

            excludeIndirectValue = !!pckg[k]?.exclude_non_direct;
            initValues[k][TargetingFields.EXCLUDE_INDIRECT] = excludeIndirectValue;

            includeOpenBiddingInventory = pckg[k]?.include_open_bidding_inventory || null;
            initValues[k][TargetingFields.INCLUDE_OPEN_BIDDING_INVENTORY] = !!includeOpenBiddingInventory;

            const rawContent = { ...(pckg[k]?.[TargetingFields.INVENTORY_AND_CONTENT] || {}) };

            inventoryAndContent = cleanUpTargetingNulls(rawContent);
            initValues[k][TargetingFields.INVENTORY_AND_CONTENT] = inventoryAndContent;

            initValues[k][TargetingFields.APP_INVENTORY] = pckg[k]?.[TargetingFields.APP_INVENTORY] || null;

            initValues[k][TargetingFields.CATEGORY] = cleanUpTargetingNulls(
              pckg[k]?.[TargetingFields.DOMAIN]?.[TargetingFields.CATEGORY]
            );

            initValues[k][TargetingFields.METACATEGORY] = cleanUpTargetingNulls(
              pckg[k]?.[TargetingFields.METACATEGORY]
            );

            const apiIq = pckg[k]?.[TargetingFields.INVENTORY_QUALITY];

            initValues[k][TargetingFields.INVENTORY_QUALITY] = apiIq ?? null;

            const isNewFormat =
              Boolean(pckg[k]?.[TargetingFields.INVENTORY_FORMAT]) &&
              !pckg[k]?.[TargetingFields.FORMAT_TYPE]?.[InventoryFormatTypeOption.AD_PLACEMENT]?.val &&
              !pckg[k]?.[TargetingFields.FORMAT_TYPE]?.[InventoryFormatTypeOption.DISTRIBUTION_CHANNEL]?.val &&
              !pckg[k]?.[TargetingFields.TECHNOLOGY_AND_DEVICES]?.[TechnologyAndDevicesOption.DEVICE_TYPE]?.val;

            initValues[k][TargetingFields.INVENTORY_FORMAT] =
              pckg[k]?.[TargetingFields.INVENTORY_FORMAT] ||
              (isNewFormat ? cloneDeep(emptyRenderingContextStructure) : null);

            if (initValues[k][TargetingFields.METACATEGORY] === null) {
              initValues[k][TargetingFields.METACATEGORY] = {
                exclude_mfa: false,
                excludes: [],
                includes: null,
                inter_dimension_operator: Criteria.ALL,
                keywords: {
                  excludes: [],
                  includes: [],
                },
              };
            }

            initValues[k][TargetingFields.TECHNOLOGY_AND_DEVICES] = cleanUpTargetingNulls(
              pckg[k]?.[TargetingFields.TECHNOLOGY_AND_DEVICES]
            );

            initValues[k][TargetingFields.GEOGRAPHIC] = cleanUpTargetingNulls(
              formatGeographic(pckg[k]?.[TargetingFields.GEOGRAPHIC])
            );

            initValues[k][TargetingFields.VIDEO] = cleanUpTargetingNulls(pckg[k]?.[TargetingFields.VIDEO]);

            const apiCustom: CustomVariablesData | null = cleanUpTargetingNulls(
              pckg[k]?.[TargetingFields.CUSTOM_VARIABLES]
            );

            const customOp = (apiCustom?.op ?? Criteria.ALL) as CriteriaValue;
            const contextualSegment = mapContextualSegmentsToFormState(
              cleanUpTargetingNulls(pckg[k]?.[contextualSegmentApiKey])
            );

            // The API can still return contextual segments in `custom` as `ctx_<provider>` attributes. Packages are
            // written with `contextual_segment_format=new`, so MGMT drops every `ctx_` entry sent in `custom`, whatever
            // its operator, and the OXTL comes from `contextual_segment` alone. None of them is shown as a custom
            // variable, since it would be lost on save.
            const apiCustomVal = Array.isArray(apiCustom?.val) ? apiCustom.val : [];
            const custom = apiCustomVal.filter(({ attr }) => !attr.startsWith(contextualSegmentPrefix));
            // Without `contextual_segment` (legacy data) the `ctx_` entries it can express are moved to contextual
            // segments, whatever `custom.op` combines them with.
            const contextual = contextualSegment.length
              ? contextualSegment
              : splitLegacyContextualSegments(apiCustomVal).contextual;

            if (custom.length) {
              initValues[k][TargetingFields.CUSTOM_VARIABLES] = { custom, op: customOp };
            }

            if (contextual.length) {
              initValues[k][TargetingFields.CONTEXTUAL_SEGMENTS] = contextual;
            }

            vtr = pckg[k]?.[TargetingFields.VIEW_THROUGH_RATE];
            initValues[k][TargetingFields.VIEW_THROUGH_RATE] =
              cleanUpTargetingNulls(vtr?.[SelectRateOption.VTR_SCORE]) || null;

            viewability = pckg[k]?.[TargetingFields.VIEWABILITY];
            initValues[k][TargetingFields.VIEWABILITY] =
              cleanUpTargetingNulls(viewability?.[SelectRateOption.VIEWABILITY_SCORE]) || null;

            contentObject = cleanUpTargetingNulls(pckg[k]?.[TargetingFields.CONTENT_OBJECT]) || null;
            initValues[k][TargetingFields.CONTENT_OBJECT] = contentObject;

            initValues[k][TargetingFields.FORMAT_TYPE] =
              cleanUpTargetingNulls(pckg[k]?.[TargetingFields.FORMAT_TYPE]) || null;
            sortFormatTypeValues(initValues[k][TargetingFields.FORMAT_TYPE]);

            initValues[k][TargetingFields.AUDIENCE] = cleanUpTargetingNulls(pckg[k]?.[TargetingFields.AUDIENCE]);
            initValues[k][TargetingFields.RETARGETING] = cleanUpTargetingNulls(pckg[k]?.[TargetingFields.RETARGETING]);
            break;
          }
          default:
            initValues[k] = pckg[k];
        }
      }
    });

    const pckgPrivMarket = pckg[PackageFields.PRIVATE_MARKET];

    if (pckgPrivMarket) {
      const initPrivMarket = initValues[PackageFields.PRIVATE_MARKET];
      const { discoverable, default_discounted_cpm, default_rate_card_cpm } = pckgPrivMarket;
      initPrivMarket.discoverable = discoverable === '1';
      initPrivMarket.default_discounted_cpm = formatPrice(default_discounted_cpm);
      initPrivMarket.default_rate_card_cpm = formatPrice(default_rate_card_cpm);
    }

    if (!pckg.targeting) {
      initValues[PackageFields.TARGETING][TargetingFields.EXCLUDE_MFA] = false;
    }
  }

  return initValues;
};

export const getHierarchyAttrsContentObjectData = (
  contentObject: ContentObjectApi,
  targetingData: Targeting,
  option: typeof ContentObjectOptions.CONTENT_RATING | typeof ContentObjectOptions.GENRE
) => {
  const targetingField = targetingData[TargetingFields.CONTENT_OBJECT]![option]!;

  const hasAllowItems = targetingField?.apiAllow || targetingField?.allow;
  const hasBlockItems = targetingField?.apiBlock || targetingField?.block;

  let newField = targetingField;

  if (hasAllowItems) {
    newField = {
      allow: contentObject[option]!.apiAllow ?? contentObject[option]!.allow,
      block: contentObject[option]!.block,
    };
  }

  if (hasBlockItems) {
    newField = {
      allow: contentObject[option]!.allow,
      block: contentObject[option]!.apiBlock ?? contentObject[option]!.block,
    };
  }

  if (newField.apiAllow) {
    delete newField.apiAllow;
  }

  if (newField.apiBlock) {
    delete newField.apiBlock;
  }

  return newField;
};

export const mapCustomVariables = (customVariables: CustomVariablesFormState | null | undefined) => {
  if (!customVariables?.custom?.length) {
    return undefined;
  }

  return { op: customVariables.op || Criteria.ALL, val: customVariables.custom };
};

export const hasOnlyAppBundleIDInventoryListCheck = (
  inventoryAndContent: Pick<InventoryAndContentApiRequest, typeof InventoryContentOption.APP_BUNDLE_ID>
): InventoryAndContentApiRequest => {
  const appBundleIdProp = inventoryAndContent?.[InventoryContentOption.APP_BUNDLE_ID];

  if (appBundleIdProp && !has(appBundleIdProp, 'val')) {
    return {
      ...inventoryAndContent,
      [InventoryContentOption.APP_BUNDLE_ID]: {
        ...appBundleIdProp,
        val: [],
      },
    };
  }

  return inventoryAndContent as InventoryAndContentApiRequest;
};

export const formStateToPackage = (refFormState: PackageFormState): Partial<PackageWithTargeting> => {
  const formState = cloneDeep(refFormState);
  const pckg: Partial<PackageWithTargeting> = {};

  let targetingData: Targeting,
    appInventory,
    inventoryAndContent,
    category,
    metacategory,
    geographic,
    technographic,
    video,
    customVariables,
    viewability,
    audience,
    formatType,
    vtr,
    excludeIndirect,
    includeOpenBiddingInventory,
    contentObject,
    inventoryFormat,
    attention,
    retargeting,
    vpvh;

  Object.keys(formState).forEach(k => {
    switch (k) {
      case PackageFields.PRIVATE_MARKET:
        break;
      case PackageFields.TARGETING: {
        targetingData = {
          ...targetingDimension[formState[k][TargetingFields.DIMENSION]],
        };
        appInventory = formState[k][TargetingFields.APP_INVENTORY];
        inventoryAndContent = formState[k][TargetingFields.INVENTORY_AND_CONTENT];
        excludeIndirect = formState[k][TargetingFields.EXCLUDE_INDIRECT];
        includeOpenBiddingInventory = formState[k][TargetingFields.INCLUDE_OPEN_BIDDING_INVENTORY];
        category = formState[k][TargetingFields.CATEGORY];
        metacategory = formState[k][TargetingFields.METACATEGORY];
        geographic = formState[k][TargetingFields.GEOGRAPHIC];
        technographic = formState[k][TargetingFields.TECHNOLOGY_AND_DEVICES];
        video = formState[k][TargetingFields.VIDEO];
        customVariables = formState[k][TargetingFields.CUSTOM_VARIABLES];
        const contextualSegments = formState[k][TargetingFields.CONTEXTUAL_SEGMENTS];
        viewability = formState[k][TargetingFields.VIEWABILITY];
        vtr = formState[k][TargetingFields.VIEW_THROUGH_RATE];
        formatType = formState[k][TargetingFields.FORMAT_TYPE];
        audience = formState[k][TargetingFields.AUDIENCE];
        contentObject = formState[k][TargetingFields.CONTENT_OBJECT];
        inventoryFormat = formState[k][TargetingFields.INVENTORY_FORMAT];
        attention = formState[k][TargetingFields.ATTENTION];
        retargeting = formState[k][TargetingFields.RETARGETING];
        vpvh = formState[k][TargetingFields.VPVH];

        if (attention) {
          targetingData[TargetingFields.ATTENTION] = attention;
        }

        if (vpvh) {
          targetingData[TargetingFields.VPVH] = vpvh;
        }

        if (isEqual(inventoryFormat, emptyRenderingContextStructure)) {
          inventoryFormat = null;
        }

        if (appInventory) {
          targetingData[TargetingFields.APP_INVENTORY] = hasOnlyAppBundleIDInventoryListCheck(appInventory);
        }

        if (inventoryAndContent) {
          targetingData[TargetingFields.INVENTORY_AND_CONTENT] =
            hasOnlyAppBundleIDInventoryListCheck(inventoryAndContent);
        }

        if (excludeIndirect) {
          targetingData[TargetingFields.EXCLUDE_INDIRECT] = excludeIndirect;
        }

        if (metacategory) {
          targetingData[TargetingFields.METACATEGORY] = metacategory;
          const { keywords } = targetingData[TargetingFields.METACATEGORY] || {};
          if (!keywords?.includes?.length && !keywords?.excludes?.length) {
            delete targetingData[TargetingFields.METACATEGORY]?.keywords;
          }
        }

        if (includeOpenBiddingInventory && excludeIndirect) {
          targetingData[TargetingFields.INCLUDE_OPEN_BIDDING_INVENTORY] = '1';
        }

        const iqQuality = formState[k][TargetingFields.INVENTORY_QUALITY] as InventoryQualityValue;

        let iqVal: string | null = null;

        if (iqQuality && typeof iqQuality === 'object' && iqQuality.iq_score?.val) {
          iqVal = iqQuality.iq_score.val || null;
        } else if (iqQuality === true) {
          iqVal = IQ_SCORE_VAL;
        }

        if (iqVal) {
          targetingData[TargetingFields.INVENTORY_QUALITY] = {
            iq_score: {
              op: '>=',
              val: iqVal,
            },
          };
        }

        if (category) {
          targetingData[TargetingFields.DOMAIN] = { [TargetingFields.CATEGORY]: category };
        }

        if (geographic) {
          targetingData[TargetingFields.GEOGRAPHIC] = geographic;
        }

        if (technographic) {
          targetingData[TargetingFields.TECHNOLOGY_AND_DEVICES] = technographic;
        }

        if (video) {
          targetingData[TargetingFields.VIDEO] = video;
        }

        if (vtr) {
          targetingData[TargetingFields.VIEW_THROUGH_RATE] = { [SelectRateOption.VTR_SCORE]: vtr };
        }

        const custom = mapCustomVariables(customVariables);
        if (custom) {
          targetingData[TargetingFields.CUSTOM_VARIABLES] = custom;
        }

        const contextualSegment = mapContextualSegmentsToApi(contextualSegments);
        if (contextualSegment) {
          targetingData[contextualSegmentApiKey] = contextualSegment;
        }

        if (viewability) {
          targetingData[TargetingFields.VIEWABILITY] = { [SelectRateOption.VIEWABILITY_SCORE]: viewability };
        }

        if (contentObject) {
          targetingData[TargetingFields.CONTENT_OBJECT] = contentObject;

          const contentObjectData = targetingData[TargetingFields.CONTENT_OBJECT];

          if (contentObjectData) {
            if (contentObjectData[ContentObjectOptions.GENRE]) {
              contentObjectData[ContentObjectOptions.GENRE] = getHierarchyAttrsContentObjectData(
                contentObject,
                targetingData,
                ContentObjectOptions.GENRE
              );
            }

            if (contentObjectData[ContentObjectOptions.CONTENT_RATING]) {
              contentObjectData[ContentObjectOptions.CONTENT_RATING] = getHierarchyAttrsContentObjectData(
                contentObject,
                targetingData,
                ContentObjectOptions.CONTENT_RATING
              );
            }
          }
        }

        if (formatType) {
          targetingData[TargetingFields.FORMAT_TYPE] = formatType;

          if (
            formatType[InventoryFormatTypeOption.DISTRIBUTION_CHANNEL] &&
            !formatType[InventoryFormatTypeOption.DISTRIBUTION_CHANNEL]?.val
          ) {
            const formatTypeObj = targetingData[TargetingFields.FORMAT_TYPE];

            if (formatTypeObj && typeof formatTypeObj === 'object') {
              delete formatTypeObj[InventoryFormatTypeOption.DISTRIBUTION_CHANNEL];
            }
          }

          if (
            formatType[InventoryFormatTypeOption.AD_PLACEMENT] &&
            !formatType[InventoryFormatTypeOption.AD_PLACEMENT]?.val
          ) {
            const formatTypeObj = targetingData[TargetingFields.FORMAT_TYPE];
            if (formatTypeObj && typeof formatTypeObj === 'object') {
              delete formatTypeObj[InventoryFormatTypeOption.AD_PLACEMENT];
            }
          }
        }

        if (audience) {
          targetingData[TargetingFields.AUDIENCE] = audience;
        }

        if (retargeting) {
          targetingData[TargetingFields.RETARGETING] = retargeting;
        }

        if (inventoryFormat) {
          targetingData[TargetingFields.INVENTORY_FORMAT] = inventoryFormat;
        }

        pckg[k] = targetingData;
        // url targeting is stored as separate package key (not included into 'targeting' object)
        pckg[TargetingFields.URL_TARGETING] = formState[k][TargetingFields.URL_TARGETING];
        break;
      }
      default:
        pckg[k] = formState[k];
    }
  });

  const formPrivMarket = formState[PackageFields.PRIVATE_MARKET];
  const { discoverable, default_discounted_cpm, default_rate_card_cpm } = formPrivMarket;

  const pckgMarket = {} as PrivateMarket;

  pckgMarket.discoverable = discoverable ? '1' : '0';
  pckgMarket.default_discounted_cpm = default_discounted_cpm || null;
  pckgMarket.default_rate_card_cpm = default_rate_card_cpm || null;

  pckg[PackageFields.PRIVATE_MARKET] = pckgMarket;

  return pckg;
};

const withNormalizedTargeting = (pckg: Partial<PackageWithTargeting>): Partial<PackageWithTargeting> =>
  pckg[PackageFields.TARGETING]
    ? { ...pckg, [PackageFields.TARGETING]: withNormalizedRenderingContext(pckg[PackageFields.TARGETING]) }
    : pckg;

export const getDiff = (
  formValues: PackageFormState,
  initialValues: PackageFormState
): Partial<PackageWithTargeting> => {
  const formValuesToApi = formStateToPackage(formValues);
  const initialValuesToApi = formStateToPackage(initialValues);

  /**
   * Compared with `rendering_context` canonicalised on both sides so that a set the UI happens to re-serialise in a
   * different order (e.g. a stored "APP,WEB" rebuilt as "WEB,APP") is not reported as a change the user never made.
   * The emitted payload below stays the raw `formValuesToApi`, so what we send to the API is unaffected.
   */
  const diff = difference(withNormalizedTargeting(formValuesToApi), withNormalizedTargeting(initialValuesToApi));
  const targetingChanged =
    diff[PackageFields.TARGETING] ||
    diff[TargetingFields.URL_TARGETING] ||
    diff[TargetingFields.URL_TARGETING] === null;

  if (targetingChanged) {
    diff[PackageFields.TARGETING] = formValuesToApi[PackageFields.TARGETING];
  }

  const estimatesChanged = !isEqual(formValuesToApi[PackageFields.ESTIMATES], initialValues[PackageFields.ESTIMATES]);
  if (estimatesChanged) {
    prepareRangeEstimationsDiff(diff, formValuesToApi);
  }

  if (
    typeof diff[PackageFields.IS_CURATED] !== 'undefined' &&
    !diff[PackageFields.IS_CURATED] &&
    initialValues[PackageFields.IS_CURATED]
  ) {
    const initialEstimatesHadValues = [
      initialValues?.estimates?.cpm?.low,
      initialValues?.estimates?.cpm?.high,
      initialValues?.estimates?.reach?.low,
      initialValues?.estimates?.reach?.high,
      initialValues?.estimates?.market_requests?.low,
      initialValues?.estimates?.market_requests?.high,
    ].some(value => !isRangeValueEmpty(value));

    if (initialEstimatesHadValues) {
      diff[PackageFields.ESTIMATES] = null;
    } else {
      delete diff[PackageFields.ESTIMATES];
    }
  }

  return diff;
};
